import { isPostcode } from '../core/rules/contact'

/**
 * Adresaanvulling voor Nederland: postcode + huisnummer → straat en plaats, via de gratis
 * PDOK Locatieserver van de overheid (BAG-gegevens, geen sleutel nodig, geen tracking).
 *
 * Bewust een hulp, geen poortwachter: geen antwoord, een fout of een time-out betekent
 * gewoon dat ze straat en plaats zelf typt. De server controleert het adres niet hierop.
 * CSP: `connect-src https://api.pdok.nl` (docs/LARAVEL.md §7.3).
 */

export type AddressSuggestion = { street: string; city: string }

const PDOK = 'https://api.pdok.nl/bzk/locatieserver/search/v3_1/free'
const TIMEOUT_MS = 3000

/** "41-2", "41 a", "41A" → het nummer zonder toevoeging (41). PDOK zoekt op het nummer. */
const numberOnly = (houseNumber: string) => houseNumber.trim().match(/^\d{1,5}/)?.[0] ?? null

type PdokDoc = { straatnaam?: unknown; woonplaatsnaam?: unknown }

/** `null` = niets gevonden of niet op te zoeken (geen NL-postcode, geen nummer, geen verbinding). */
export async function lookupAddress(postcode: string, houseNumber: string, signal?: AbortSignal): Promise<AddressSuggestion | null> {
  const pc = postcode.replace(/\s+/g, '').toUpperCase()
  const nr = numberOnly(houseNumber)
  if (!isPostcode(pc, 'NL') || !nr) return null

  const timeout = new AbortController()
  const timer = window.setTimeout(() => timeout.abort(), TIMEOUT_MS)
  const onAbort = () => timeout.abort()
  signal?.addEventListener('abort', onAbort)
  try {
    const params = new URLSearchParams({ q: `postcode:${pc} and huisnummer:${nr}`, fq: 'type:adres', rows: '1', fl: 'straatnaam,woonplaatsnaam' })
    const res = await fetch(`${PDOK}?${params}`, { signal: timeout.signal, credentials: 'omit' })
    if (!res.ok) return null
    const data: unknown = await res.json()
    const doc = (data as { response?: { docs?: PdokDoc[] } })?.response?.docs?.[0]
    if (!doc || typeof doc.straatnaam !== 'string' || typeof doc.woonplaatsnaam !== 'string') return null
    return { street: doc.straatnaam, city: doc.woonplaatsnaam }
  } catch {
    return null
  } finally {
    window.clearTimeout(timer)
    signal?.removeEventListener('abort', onAbort)
  }
}
